import { z } from "zod";

// =============================================================================
// Qlik App Performance Evaluation (the tenant's own evaluator, /api/analytics)
//
// These schemas mirror the Go structs in apps/worker/internal/qlik/evaluations.go
// field for field, because what lands in the `result` jsonb is exactly what
// those structs marshal. The contract was fixed by a live probe
// (docs/plans/qlik-app-evaluation-spike.md); where Qlik's published spec and
// the real payload disagree, the real payload wins — divergences marked inline.
//
// Everything is LIBERAL on input: Qlik omits and nulls fields freely, and Go's
// `omitempty` drops zero values on the way out, so an absent number really does
// mean 0 and an absent string really does mean "". Nothing outside `id` and
// `status` is required — a field the probe never saw must never fail a parse.
// =============================================================================

const optionalText = z
  .string()
  .nullish()
  .transform((v) => v ?? "");

const optionalNumber = z
  .number()
  .nullish()
  .transform((v) => v ?? 0);

const optionalBoolean = z
  .boolean()
  .nullish()
  .transform((v) => v ?? false);

// --- Row lifecycle (ours, not Qlik's) ---

// 'forbidden' (Qlik's per-app/space 403) and 'no_public_sheets'
// (WARN-NO-OBJECTS) are DATA STATES, not failures: the app simply has no
// evaluation to show.
export const qlikEvaluationStatusSchema = z.enum([
  "pending",
  "running",
  "completed",
  "failed",
  "forbidden",
  "timeout",
  "no_public_sheets",
]);
export type QlikEvaluationStatus = z.infer<typeof qlikEvaluationStatusSchema>;

export const qlikEvaluationTimeoutSecSchema = z.number().int().min(1).max(86400);

// Worker handoff at queue time, independent of static analysis completion.
export const openQlikEvaluationSchema = z.object({
  organizationId: z.string().min(1),
  connectionId: z.string().uuid(),
  appId: z.string().min(1),
  analysisId: z.string().uuid().optional(),
  qlikEvaluationId: z.string().min(1).optional(),
  qlikEvaluationStatus: z.enum(["forbidden", "failed", "timeout"]).optional(),
  qlikEvaluationError: z.string().max(500).optional(),
  qlikEvaluationStartedAt: z.string().datetime().optional(),
  qlikEvaluationTimeoutSec: qlikEvaluationTimeoutSecSchema.optional(),
});
export type OpenQlikEvaluationInput = z.infer<typeof openQlikEvaluationSchema>;

// --- Payload pieces ---

// One evaluated object or sheet.
export const qlikEvaluationObjectSpecSchema = z.object({
  // The object's qId — the SAME key as AnalysisObject.id from the static
  // analyzer, so Qlik's timings join our objects directly, no title heuristic.
  id: z.string(),
  title: optionalText,
  sheetId: optionalText,
  objectType: optionalText,
});
export type QlikEvaluationObjectSpec = z.infer<typeof qlikEvaluationObjectSpecSchema>;

// Documented by the spec, absent from every real payload — optional, and
// nothing may depend on it.
export const qlikEvaluationObjectPassSchema = z.object({
  cpuSeconds: z
    .array(z.number())
    .nullish()
    .transform((v) => v ?? []),
  cpuQuotients: z
    .array(z.number())
    .nullish()
    .transform((v) => v ?? []),
  responseTimeSeconds: optionalNumber,
});
export type QlikEvaluationObjectPass = z.infer<typeof qlikEvaluationObjectPassSchema>;

// An object (or a sheet) with its measured response time. Qlik computes a sheet
// in one batch and attributes the batch time to its objects, so times within a
// sheet are near-identical by construction — not per-object cost.
export const qlikEvaluationObjectTimingSchema = qlikEvaluationObjectSpecSchema.extend({
  responseTimeSeconds: optionalNumber,
  timeoutStatusCode: optionalText, // e.g. CALC-TIMEOUT
  passes: z
    .array(qlikEvaluationObjectPassSchema)
    .nullish()
    .transform((v) => v ?? []),
});
export type QlikEvaluationObjectTiming = z.infer<typeof qlikEvaluationObjectTimingSchema>;

export const qlikEvaluationObjectMemorySchema = qlikEvaluationObjectSpecSchema.extend({
  memoryLimitStatusCode: optionalText, // e.g. OUT-OF-MEMORY
});
export type QlikEvaluationObjectMemory = z.infer<typeof qlikEvaluationObjectMemorySchema>;

export const qlikEvaluationObjectCpuSchema = qlikEvaluationObjectSpecSchema.extend({
  cpuQuotient1: optionalNumber,
});
export type QlikEvaluationObjectCpu = z.infer<typeof qlikEvaluationObjectCpuSchema>;

// One published sheet. `sheetObjects` carries only the 5 SLOWEST objects of the
// sheet (35 of 236 in the probed app): an object outside that slice has NO
// time, which is not the same as zero.
export const qlikEvaluationSheetSchema = z.object({
  sheet: qlikEvaluationObjectTimingSchema,
  objectCount: optionalNumber,
  sheetObjects: z
    .array(qlikEvaluationObjectTimingSchema)
    .nullish()
    .transform((v) => v ?? []),
});
export type QlikEvaluationSheet = z.infer<typeof qlikEvaluationSheetSchema>;

// topTablesByBytes / topFieldsByBytes (5 each). The real payload returns
// `byteSize` in camelCase — the spec's `byte_size` is wrong — while `is_system`
// really is snake_case.
export const qlikEvaluationTopItemSchema = z.object({
  name: optionalText,
  byteSize: optionalNumber,
  is_system: optionalBoolean,
});
export type QlikEvaluationTopItem = z.infer<typeof qlikEvaluationTopItemSchema>;

export const qlikEvaluationOpenAppStepSchema = z.object({
  name: optionalText,
  durationMilliseconds: optionalNumber,
});
export type QlikEvaluationOpenAppStep = z.infer<typeof qlikEvaluationOpenAppStepSchema>;

export const qlikEvaluationOpenAppSchema = z.object({
  steps: z
    .array(qlikEvaluationOpenAppStepSchema)
    .nullish()
    .transform((v) => v ?? []),
  totalDurationMilliseconds: optionalNumber,
});
export type QlikEvaluationOpenApp = z.infer<typeof qlikEvaluationOpenAppSchema>;

// A `finished` evaluation does NOT mean every object was evaluated: the probed
// app finished carrying 17 `notevaluated` / INFO-CALC-COND events. Always read
// the events.
export const qlikEvaluationEventSchema = z.object({
  severity: optionalText,
  errorCode: optionalText,
  objectId: optionalText,
  objectType: optionalText,
  sheetId: optionalText,
  sheetTitle: optionalText,
  objectTitle: optionalText,
  objectVisualization: optionalText,
  // This namespace's rename of v1's `details`.
  message: optionalText,
});
export type QlikEvaluationEvent = z.infer<typeof qlikEvaluationEventSchema>;

export const qlikEvaluationEngineSchema = z.object({
  shortName: optionalText, // e.g. "Standard-20"
});
export type QlikEvaluationEngine = z.infer<typeof qlikEvaluationEngineSchema>;

// The measurement block. Even an app with no published sheet still returns
// rowCount, documentSizeMiB and the two top-5 byte lists.
export const qlikEvaluationResultSchema = z.object({
  sheetCount: optionalNumber,
  objectCount: optionalNumber,
  rowCount: optionalNumber,
  documentSizeMiB: optionalNumber,
  hasSectionAccess: optionalBoolean,
  openApp: qlikEvaluationOpenAppSchema.nullish(),
  // Replaced v1's single `sheets` list.
  sheetsCached: z
    .array(qlikEvaluationSheetSchema)
    .nullish()
    .transform((v) => v ?? []),
  sheetsUncached: z
    .array(qlikEvaluationSheetSchema)
    .nullish()
    .transform((v) => v ?? []),
  objSlowCached: z
    .array(qlikEvaluationObjectTimingSchema)
    .nullish()
    .transform((v) => v ?? []),
  objSlowUncached: z
    .array(qlikEvaluationObjectTimingSchema)
    .nullish()
    .transform((v) => v ?? []),
  // Came back empty in the probe — shape known from the spec only.
  objNoCache: z
    .array(qlikEvaluationObjectTimingSchema)
    .nullish()
    .transform((v) => v ?? []),
  objMemoryLimit: z
    .array(qlikEvaluationObjectMemorySchema)
    .nullish()
    .transform((v) => v ?? []),
  objSingleThreaded: z
    .array(qlikEvaluationObjectCpuSchema)
    .nullish()
    .transform((v) => v ?? []),
  topTablesByBytes: z
    .array(qlikEvaluationTopItemSchema)
    .nullish()
    .transform((v) => v ?? []),
  topFieldsByBytes: z
    .array(qlikEvaluationTopItemSchema)
    .nullish()
    .transform((v) => v ?? []),
});
export type QlikEvaluationResult = z.infer<typeof qlikEvaluationResultSchema>;

// One run of Qlik's evaluator, as stored in the row's `result` jsonb.
export const qlikEvaluationPayloadSchema = z.object({
  id: optionalText, // 24-hex ObjectID, not a uuid
  appId: optionalText,
  appItemId: optionalText,
  appName: optionalText,
  tenantId: optionalText,
  // Qlik's own status: enqueued → running → finished | finishedwithwarnings.
  // Distinct from our row status; never compare it against a closed set.
  status: optionalText,
  started: optionalText, // "0001-01-01T00:00:00Z" while enqueued
  ended: optionalText,
  timestamp: optionalText,
  version: optionalNumber,
  engine: qlikEvaluationEngineSchema.nullish(),
  // Shape unconfirmed (never populated in the probe) — kept opaque on purpose:
  // inventing a structure here would fail the whole parse the day Qlik fills it.
  openAppProgress: z.unknown(),
  events: z
    .array(qlikEvaluationEventSchema)
    .nullish()
    .transform((v) => v ?? []),
  result: qlikEvaluationResultSchema.nullish(),
  // Replaced v1's `metadata`; null in every probed evaluation, shape
  // unconfirmed — opaque, and nothing may depend on it.
  reloadInformation: z.unknown(),
});
export type QlikEvaluationPayload = z.infer<typeof qlikEvaluationPayloadSchema>;

// --- The stored row, as the API returns it ---

export const qlikEvaluationSchema = z.object({
  id: z.string().uuid(),
  status: qlikEvaluationStatusSchema,
  // Null while 'pending' (the row exists before the POST that mints the id) and
  // forever on 'forbidden'.
  evaluationId: z.string().nullish(),
  result: qlikEvaluationPayloadSchema.nullish(),
  error: z.string().nullish(),
  requestedAt: z.string().nullish(),
  startedAt: z.string().nullish(),
  finishedAt: z.string().nullish(),
  createdAt: z.string(),
});
export type QlikEvaluation = z.infer<typeof qlikEvaluationSchema>;

// --- Worker callback (POST /api/internal/worker/qlik-evaluations/:id) ---

// The poller's write-back. This is the ONLY gate on the `result` jsonb: the read
// path just projects the column, so a malformed payload rejected here is a
// malformed payload that never gets stored.
export const saveQlikEvaluationResultSchema = z.object({
  organizationId: z.string(),
  status: qlikEvaluationStatusSchema,
  result: qlikEvaluationPayloadSchema.nullish(),
  error: z.string().max(2000).nullish(),
  // Validated, not free-form: the service turns these into Date objects, and
  // `new Date("garbage")` would store an Invalid Date (a 500) instead of a 400.
  startedAt: z.string().datetime().nullish(),
  finishedAt: z.string().datetime().nullish(),
});
export type SaveQlikEvaluationResult = z.infer<typeof saveQlikEvaluationResultSchema>;

// --- Cross-app ranking (GET /api/v1/analyses/evaluations/ranking) ---

export const qlikEvaluationRankingQuerySchema = z.object({
  connectionId: z.string().uuid(),
});
export type QlikEvaluationRankingQuery = z.infer<typeof qlikEvaluationRankingQuerySchema>;

// One app's latest COMPLETED evaluation, flattened to the handful of numbers
// that survive a cross-app comparison. Everything here is a whole-app or
// whole-sheet measurement: per-object times are deliberately absent, because
// Qlik attributes a sheet's batch time to its objects and only covers the
// top 5 of each sheet — nothing you can rank apps by.
export const qlikEvaluationRankingItemSchema = z.object({
  evaluationRowId: z.string().uuid(),
  appId: z.string(),
  appName: z.string(),
  // The analysis this evaluation was attached to, when it still exists (the FK
  // is ON DELETE SET NULL) — the row links straight to it.
  analysisId: z.string().uuid().nullable(),
  evaluatedAt: z.string(),
  sheetCount: z.number(),
  objectCount: z.number(),
  rowCount: z.number(),
  documentSizeMiB: z.number(),
  // Nullable on purpose: an evaluation without an openApp block, or without a
  // single published sheet, measured NOTHING here — and "0 ms" would read as an
  // instant app. Absence is rendered as absence.
  openAppMs: z.number().nullable(),
  // Slowest published sheet, cold and cached. The cold number is the ranking
  // key: it is the closest thing Qlik gives to "how slow is this app".
  slowestSheetSeconds: z.number().nullable(),
  slowestSheetCachedSeconds: z.number().nullable(),
  // Objects Qlik skipped (`notevaluated` events, e.g. INFO-CALC-COND). A
  // `finished` evaluation with a count here measured less than it looks.
  notEvaluatedCount: z.number(),
});
export type QlikEvaluationRankingItem = z.infer<typeof qlikEvaluationRankingItemSchema>;

export const qlikEvaluationRankingResponseSchema = z.object({
  data: z.array(qlikEvaluationRankingItemSchema),
});
export type QlikEvaluationRankingResponse = z.infer<typeof qlikEvaluationRankingResponseSchema>;
